import React from "react";
import Option, { OptionGroup } from "@ui/Option";
import t from "../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import downloadPage from "@ui/lib/downloadPage";
import { buildShareableURL2 } from "@ui/lib/urlTools";
import type { User } from "../../../api/useUser";
import { useCanUserDo, useCanUserDoOnSomeProject } from "../../canUserDo";
import {
  Database2Line,
  ExternalLinkLine,
  Jupyter,
  LineChartLine,
  LinksLine,
  ListSettingsLine,
  LogoutCircleRLine,
  Screenshot2Line,
  TimeLine,
  UserLine,
} from "@ui/IconV2/react";
import { MTS_ENV } from "../../../lib/setupEnv";
import { styled } from "@linaria/react";
import { colors, fonts } from "@ui/Theme";
import { useSettings } from "../../../api/useSettings";
import GitRepositoryLine from "@ui/IconV2/react/GitRepositoryLine";
import { type LocalExternalLink } from "../../../api/useExternalLinks";
import Ellipsis from "@ui/Ellipsis";
import { useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import { toPath } from "../../../router/helpers/path";
import { Tag } from "@ui/Tag";
import Switch from "@ui/Switch";
import TooltipV2 from "@ui/TooltipV2";
import { css } from "@linaria/core";
import useSetupMode from "../../SetupMode/useSetupMode";
import { getGlobalRoleName } from "../../Administration/Users/globalRoleOptions";

const StyledOption = styled(Option)`
  border-radius: 4px;
  gap: 12px;
  padding: 8px;
  min-width: 216px;

  & > svg {
    width: unset;
    margin-right: unset;
    color: ${colors.titanium800};
  }
`;

const UserGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const UserName = styled.span`
  font: ${fonts.body2SemiBold};
  color: ${colors.carbon800};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const setupModeSwitch = css`
  padding: 8px;
  min-width: 216px;
`;

const setupModeTooltip = css`
  && {
    max-width: 265px;
  }
`;

const AppHeaderMenu = function ({
  externalLinks,
  openUserModal,
  user,
}: {
  externalLinks: LocalExternalLink[];
  openUserModal: () => void;
  user: User;
}) {
  const auth = useAuth();
  const { canEnableSetupMode, isSetupModeEnabled, setSetupModeEnabled } = useSetupMode();
  const location = useLocation();
  const settings = useSettings();
  const { addToast } = useToasts();
  const allowSourceRead = useCanUserDo("SOURCE_READ");
  const allowProjectSourceRead = useCanUserDoOnSomeProject("SOURCE_READ");
  const allowAdministration = useCanUserDo("ADMIN_USER_MANAGE");
  const allowJupyterRead = useCanUserDoOnSomeProject("JUPYTER_ACCESS");
  const allowProjectUserManage = useCanUserDoOnSomeProject("USER_MANAGE");
  const allowProjectUser = useCanUserDoOnSomeProject("CATEGORY_EDIT");
  const allowProjectRead = useCanUserDoOnSomeProject("PROJECT_READ");
  const allowRead = useCanUserDo("PROJECT_READ");
  const hasGlobalRole = user.roles.global !== null && user.roles.global !== "DIAGNOSER";
  const projectRolesCount = user.roles.projects.length;
  const roleTagLabel = hasGlobalRole
    ? getGlobalRoleName(user.roles.global)
    : projectRolesCount > 0
      ? t("hamburger.user.projects_count", { count: projectRolesCount })
      : null;

  const handleViewportScreenshotDownload = React.useCallback(() => {
    const root = document.getElementById("root");

    if (!root) throw new Error("Root element not found");
    const closeInfo = addToast("info", t("hamburger.option.print.toast.in_progress"));
    window.setTimeout(async () => {
      try {
        await downloadPage(root);
        closeInfo();
        addToast("success", t("hamburger.option.print.toast.success"));
      } catch (e) {
        closeInfo();
        addToast("error", t("hamburger.option.print.toast.error"));
        throw e;
      }
    }, 500); // 500ms needed for menu to close and toast to show up (ressource heavy)
  }, [addToast]);

  const handlePermalinkCopyToClipboard = React.useCallback(async () => {
    const result = buildShareableURL2();
    await window.navigator.clipboard.writeText(result);
    addToast("success", t("hamburger.option.permalink.toast.success"));
  }, [addToast]);

  return (
    <>
      {canEnableSetupMode && (
        <OptionGroup>
          <TooltipV2 className={setupModeTooltip} overlay={t("hamburger.setup_mode.tooltip")} focus>
            <div>
              <Switch
                className={setupModeSwitch}
                position="right"
                justify
                checked={isSetupModeEnabled}
                onChange={event => setSetupModeEnabled(event.currentTarget.checked)}
              >
                {t("hamburger.setup_mode")}
              </Switch>
            </div>
          </TooltipV2>
        </OptionGroup>
      )}
      <OptionGroup>
        <StyledOption closeOnClick onClick={handleViewportScreenshotDownload}>
          <Screenshot2Line />
          {t("hamburger.option.print")}
        </StyledOption>
        <StyledOption closeOnClick onClick={handlePermalinkCopyToClipboard}>
          <LinksLine />
          {t("hamburger.option.permalink")}
        </StyledOption>
      </OptionGroup>

      {(allowRead ||
        allowProjectRead ||
        allowSourceRead ||
        allowProjectSourceRead ||
        allowJupyterRead) && (
        <OptionGroup>
          {(allowRead || allowProjectRead || allowSourceRead || allowProjectSourceRead) &&
            settings.FLEET_VIEW_ENABLED && (
              <StyledOption
                closeOnClick
                to={toPath("/reporting/performance", {})}
                selected={location.pathname === "/reporting"}
              >
                <LineChartLine />
                {t("reporting.header.title")}
              </StyledOption>
            )}
          {allowSourceRead && (
            <StyledOption closeOnClick to={toPath("/sources", {})}>
              <Database2Line />
              {t("hamburger.option.datasources")}
            </StyledOption>
          )}
          {allowJupyterRead && (
            <StyledOption
              closeOnClick
              href={`${window.location.origin}/jupyter/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Jupyter />
              {t("hamburger.option.jupyterHub")}
            </StyledOption>
          )}
        </OptionGroup>
      )}

      <OptionGroup>
        {(allowAdministration || allowProjectUserManage || allowProjectUser) && (
          <StyledOption
            closeOnClick
            to={
              allowAdministration || allowProjectUserManage
                ? toPath("/administration", {})
                : toPath("/administration/classification", {})
            }
            selected={
              location.pathname === "/administration" ||
              location.pathname === "/administration/users" ||
              location.pathname === "/administration/classification/quantities" ||
              location.pathname === "/administration/classification/failures"
            }
          >
            <ListSettingsLine />
            {t("administration.header.title")}
          </StyledOption>
        )}

        {(allowRead || allowProjectRead) && (
          <StyledOption closeOnClick to={toPath("/activity", {})}>
            <TimeLine />
            {t("hamburger.activity.title")}
          </StyledOption>
        )}
      </OptionGroup>
      {externalLinks.length > 0 && (
        <OptionGroup>
          {externalLinks.map(externalLink => (
            <StyledOption
              key={externalLink.id}
              href={externalLink.url}
              target="_blank"
              rel="noopener noreferrer"
              closeOnClick
            >
              <ExternalLinkLine />
              <Ellipsis maxNumberOfLines={1} style={{ maxWidth: "200px" }}>
                {externalLink.localName}
              </Ellipsis>
            </StyledOption>
          ))}
        </OptionGroup>
      )}
      <OptionGroup
        title={
          <UserGroupTitle>
            <UserName>
              {t("hamburger.group_title.user_name", {
                name: user.name ?? user.login,
              })}
            </UserName>
            {roleTagLabel && <Tag size="s">{roleTagLabel}</Tag>}
          </UserGroupTitle>
        }
      >
        <StyledOption closeOnClick onClick={openUserModal}>
          <UserLine />
          {t("hamburger.user.profile")}
        </StyledOption>
        {settings.LEGAL_FILE_PATH.length > 0 && (
          <StyledOption
            href={MTS_ENV.COREV2_ASSETS_URL + "/" + settings.LEGAL_FILE_PATH}
            target="_blank"
            rel="noopener noreferrer"
            closeOnClick
          >
            <GitRepositoryLine />
            {t("hamburger.legals.title")}
          </StyledOption>
        )}
        <StyledOption closeOnClick onClick={() => auth.signoutRedirect()}>
          <LogoutCircleRLine />
          {t("hamburger.user.logout")}
        </StyledOption>
      </OptionGroup>
    </>
  );
};

export default React.memo(AppHeaderMenu);
